import React, { ReactNode, useCallback, useMemo } from 'react';
import { getPlatformStyles } from '@direct-frontend/platform-code';
import { genericMemo } from '@direct-frontend/stdlib';

import { cn } from '../../lib/css/className/className';
import { TextGeneric } from '../TextGeneric/TextGeneric';
import { HStack, Stack, VStack } from '../Stack';
import { IconEdit, IconVerticalMenu } from '../../icons';
import { Button } from '../Button';
import { ClickableIcon } from '../ClickableIcon';
import { HelpProps } from '../Help';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { Breadcrumbs } from '../Breadcrumbs';

import { PageHeadWrapper } from './PageHeadWrapper/PageHeadWrapper';
import { PageHeadImage } from './PageHeadImage/PageHeadImage';
import { PageHeadProps, RenderMenuProps } from './types';
import { isAddonLeftTuple, normalizeButtons } from './helpers';

getPlatformStyles({ desktop: import('./PageHead@desktop.css') });

const cls = cn('PageHead');

/**
 * Единообразный компонент для отображения заголовка страницы
 * @kind component
 */
const PageHead = genericMemo(function PageHead({
    className,
    title,
    help,
    buttonProps,
    addonRight,
    addonBottomRight,
    caption,
    labels,
    breadcrumbs,
    renderTabMenu,
    onEditClick,
    addonLeft,
    image,
    renderMenu,
    'data-testid': dataTestId = 'PageHead',
}: PageHeadProps) {
    const skinClass = getColorSkin('gray-neutral', {});
    const menuProps: RenderMenuProps = useMemo(
        () => ({
            trigger: (
                <Button
                    color="contour"
                    size="xs"
                    icon={IconVerticalMenu}
                    data-testid={`${dataTestId}.MenuTrigger`}
                    logNode="PageHeadMenuTrigger"
                />
            ),
            placement: 'bottom-end',
            'data-testid': `${dataTestId}.Menu`,
            size: 'm',
        }),
        [dataTestId],
    );

    const menu = renderMenu?.(menuProps);
    const hasMenu = Boolean(renderMenu);
    const isEditable = Boolean(onEditClick);
    const buttons = normalizeButtons(buttonProps);
    const hasButtons = buttons.length > 0;
    // TODO DIRECT-272548 Поддержать второй addonLeft на десктопе. Пока используем только первый.
    const mainAddonLeft = addonLeft && (isAddonLeftTuple(addonLeft) ? addonLeft[0] : addonLeft);
    const onlyTabMenu = Boolean(
        renderTabMenu && !addonRight && !hasButtons && !hasMenu && !breadcrumbs && !title && !caption && !labels,
    );

    const titleAddon = useCallback(
        (helpProps: HelpProps) =>
            isEditable ? (
                <ClickableIcon
                    className={helpProps.iconContainerClassName}
                    data-testid={`${dataTestId}.EditName.Button`}
                    color="current"
                    onClick={onEditClick}
                    logNode="PageHeadEditNameButton"
                >
                    <IconEdit size={helpProps.size} />
                </ClickableIcon>
            ) : (
                help && help(helpProps)
            ),
        [isEditable, dataTestId, onEditClick, help],
    );

    const addonRightElement = (hasButtons || hasMenu || addonRight) && (
        <HStack gap="8">
            {addonRight}
            {buttons.map((props, index) => (
                <Button
                    key={`${props.href ? 'link' : 'button'}-${index}`}
                    data-testid={index === 0 ? `${dataTestId}.MainActionButton` : `${dataTestId}.SecondaryActionButton`}
                    color="contour"
                    {...props}
                    size="xs"
                />
            ))}
            {menu}
        </HStack>
    );

    const { addonClasses } = useInternalVerticalAlignWithSkin({
        textSize: 'current-main',
        addonType: 'universal',
    });

    const hasContent = image || breadcrumbs || title || caption || labels;

    const tabMenuProps = {
        hasTopPadding: true,
        isUnderlined: false,
        'data-testid': `${dataTestId}.TabMenu`,
    };

    const renderTabMenuContent = (extraProps = {}) => {
        if (!renderTabMenu) return <div />;
        return renderTabMenu({ ...tabMenuProps, ...extraProps, logNode: 'PageHeadTabMenu' });
    };

    let bottomBlockContent: ReactNode;

    if (addonBottomRight) {
        bottomBlockContent = (
            <HStack gap="16" className={cls('tabMenu')} alignItems="center" justifyContent="space-between">
                {renderTabMenuContent()}
                {addonBottomRight}
            </HStack>
        );
    } else {
        bottomBlockContent = renderTabMenuContent({ className: cls('tabMenu') });
    }

    return (
        <PageHeadWrapper className={cls({ 'only-tabs': onlyTabMenu }, [className, skinClass])} data-testid={dataTestId}>
            {hasContent && (
                <HStack gap="12" alignItems="center">
                    <PageHeadImage image={image} />
                    <VStack className={cls('content')}>
                        {breadcrumbs && (
                            <HStack gap="8" className={cls('breadcrumbs')}>
                                {mainAddonLeft?.({ size: '2xs', color: 'text-supplementary' })}
                                <Breadcrumbs list={breadcrumbs} className={cls('breadcrumbs-text')} />
                            </HStack>
                        )}
                        {title && (
                            <HStack gap="8">
                                <TextGenericWithSkin
                                    data-testid={`${dataTestId}.Title`}
                                    skinSize="layout"
                                    textSize="current-main"
                                    bold
                                    color="current"
                                    help={titleAddon}
                                    className={cls('header')}
                                    as="h2"
                                >
                                    {title}
                                </TextGenericWithSkin>
                                <Stack className={addonClasses}>{addonRightElement}</Stack>
                            </HStack>
                        )}
                        {(caption || labels) && (
                            <HStack gap="8" alignItems="flex-start" className={cls('caption-row')}>
                                {labels?.({
                                    labelSize: '3xs',
                                    iconSize: '12',
                                })}
                                {caption && (
                                    <TextGeneric textSize="14-20" color="current" role="supplementary">
                                        {caption}
                                    </TextGeneric>
                                )}
                            </HStack>
                        )}
                    </VStack>
                </HStack>
            )}
            {bottomBlockContent}
        </PageHeadWrapper>
    );
});

export default PageHead;
